import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import DOMPurify from "dompurify";
import { Task, FileAsset, User, Role, TaskVisibility } from "../types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Identify if task is an origin (root) task
export function isOriginTask(task: Task, allTasks: Task[]): boolean {
  // No dependencies AND no series parent
  const hasNoDependencies = !task.dependsOn || task.dependsOn.length === 0;
  const hasNoSeriesParent = !allTasks.some(t => t.childBottomId === task.id);
  const hasNoParallelParent = !allTasks.some(t => 
    t.childLeftIds.includes(task.id) || t.childRightIds.includes(task.id)
  );
  return hasNoDependencies && hasNoSeriesParent && hasNoParallelParent;
}

// Find all predecessor tasks
export function getPredecessors(task: Task, allTasks: Task[]): Task[] {
  const predecessors: Task[] = [];
  
  // From blocking dependencies
  if (task.dependsOn) {
    task.dependsOn.forEach(depId => {
      const depTask = allTasks.find(t => t.id === depId);
      if (depTask) predecessors.push(depTask);
    });
  }
  
  // From series parent
  const seriesParent = allTasks.find(t => t.childBottomId === task.id);
  if (seriesParent) predecessors.push(seriesParent);
  
  // From parallel parent (task that spawned this as sibling)
  const parallelParent = allTasks.find(t => 
    t.childLeftIds.includes(task.id) || t.childRightIds.includes(task.id)
  );
  if (parallelParent) predecessors.push(parallelParent);
  
  return predecessors;
}

// Calculate due date for a task
export function calculateDueDate(task: Task, allTasks: Task[]): string | undefined {
  // Origin tasks use manual due date
  if (isOriginTask(task, allTasks)) {
    return task.endDate;
  }
  
  // Get all predecessors
  const predecessors = getPredecessors(task, allTasks);
  if (predecessors.length === 0) {
    return task.endDate; // Fallback to manual date if no predecessors
  }
  
  // Find the latest predecessor due date
  const predecessorDueDates = predecessors
    .map(p => calculateDueDate(p, allTasks)) // Recursive for chain calculation
    .filter(Boolean) as string[];
  
  if (predecessorDueDates.length === 0) {
    return task.endDate; // No valid predecessor dates
  }
  
  // Sort dates and get the latest
  const latestPredecessorDate = predecessorDueDates.sort().reverse()[0];
  
  // Subtract lead time to get this task's due date
  const leadTime = task.duration || 0;
  const dueDate = new Date(latestPredecessorDate);
  dueDate.setDate(dueDate.getDate() - leadTime);
  
  return dueDate.toISOString().split('T')[0]; // YYYY-MM-DD format
}

// Calculate lead time from due date
export function calculateLeadTime(task: Task, newDueDate: string, allTasks: Task[]): number {
  const predecessors = getPredecessors(task, allTasks);
  if (predecessors.length === 0) return task.duration || 0;
  
  // Get latest predecessor due date
  const predecessorDueDates = predecessors
    .map(p => calculateDueDate(p, allTasks))
    .filter(Boolean) as string[];
  
  if (predecessorDueDates.length === 0) return task.duration || 0;
  
  const latestPredecessorDate = predecessorDueDates.sort().reverse()[0];
  
  // Calculate difference in days
  const due = new Date(newDueDate);
  const predecessor = new Date(latestPredecessorDate);
  const diffTime = predecessor.getTime() - due.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return Math.max(0, diffDays); // Ensure non-negative
}

// Get all descendant tasks (for propagation)
export function getDescendants(taskId: string, allTasks: Task[]): Task[] {
  const descendants: Task[] = [];
  const visited = new Set<string>();
  const queue = [taskId];
  
  while (queue.length > 0) {
    const currentId = queue.shift()!;
    if (visited.has(currentId)) continue;
    visited.add(currentId);
    
    // Find tasks that depend on current task
    allTasks.forEach(task => {
      if (task.id === currentId) return; // Skip self
      
      // Check if task depends on current
      const dependsOnCurrent = task.dependsOn?.includes(currentId);
      const currentTask = allTasks.find(t => t.id === currentId);
      const isSeriesChild = currentTask?.childBottomId === task.id;
      const isParallelChild = currentTask?.childLeftIds.includes(task.id) ||
                               currentTask?.childRightIds.includes(task.id);
      
      if (dependsOnCurrent || isSeriesChild || isParallelChild) {
        descendants.push(task);
        queue.push(task.id);
      }
    });
  }
  
  return descendants;
}

// Propagate date changes to all descendants
export function propagateDateChanges(taskId: string, allTasks: Task[]): Task[] {
  const descendants = getDescendants(taskId, allTasks);
  const updatedTasks = [...allTasks];
  
  descendants.forEach(descendant => {
    const taskIndex = updatedTasks.findIndex(t => t.id === descendant.id);
    if (taskIndex === -1) return;
    
    const newDueDate = calculateDueDate(descendant, updatedTasks);
    if (newDueDate) {
      updatedTasks[taskIndex] = {
        ...updatedTasks[taskIndex],
        endDate: newDueDate
      };
    }
  });
  
  return updatedTasks;
}

// Format date for display
export function formatDate(dateString: string | undefined): string {
  if (!dateString) return 'Not set';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Check if task is overdue
export function isOverdue(task: Task): boolean {
  if (!task.endDate || task.status === 'COMPLETE') return false;
  const due = new Date(task.endDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due < today;
}

/**
 * Normalize project name for duplicate checking
 * - Converts to lowercase
 * - Trims whitespace
 * - Collapses multiple spaces to single space
 */
export const normalizeProjectName = (name: string): string => {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
};

/**
 * Check if a project name is a duplicate (case-insensitive, whitespace-normalized)
 */
export const isDuplicateProjectName = (
  name: string, 
  existingProjects: Array<{ name: string; id?: string }>,
  excludeId?: string
): boolean => {
  const normalized = normalizeProjectName(name);
  return existingProjects.some(p => 
    (!excludeId || p.id !== excludeId) && 
    normalizeProjectName(p.name) === normalized
  );
};

/**
 * Check if user can view a file based on permissions
 */
export function canUserViewFile(file: FileAsset, user: User, task?: Task): boolean {
  // Admin/PM have full access
  if (user.role === Role.ADMIN || user.role === Role.PM) {
    return true;
  }
  
  // File uploader always has access
  if (file.uploadedBy === user.id) {
    return true;
  }
  
  // Check task assignees if file linked to task
  if (task && task.assigneeIds?.includes(user.id)) {
    return true;
  }
  
  // Public files visible to all project members
  if (!file.visibility || file.visibility === 'Public') {
    return true;
  }
  
  // Check explicit viewerIds
  if (file.viewerIds?.includes(user.id)) {
    return true;
  }
  
  // Check viewerType filters
  if (file.viewerType) {
    switch (file.viewerType) {
      case 'Brand':
        return user.role !== Role.FACTORY;
      case 'Factory':
        return user.role === Role.FACTORY;
      case 'Team':
        return file.viewerTeams?.includes(user.department || '') || false;
      case 'Assignee':
        return task?.assigneeIds?.includes(user.id) || false;
      case 'Custom':
        return file.viewerIds?.includes(user.id) || false;
    }
  }
  
  // Semi-private: show to project members
  if (file.visibility === 'Semi-Private') {
    return true; // Assuming user passed project-level access
  }
  
  // Private: deny by default
  return false;
}

/**
 * Sanitize HTML content to prevent XSS attacks
 * Allows basic formatting tags (bold, italic, links)
 */
export function sanitizeHtml(dirty: string): string {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'br', 'p'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  });
}

/**
 * Sanitize plain text content (removes all HTML)
 * Use this for user input that should be displayed as plain text
 */
export function sanitizeText(text: string): string {
  if (!text) return '';
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
}